import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import React, { useState } from "react";
import { map } from "lodash";

import IconOnlyLink from "~/components/links/IconOnlyLink";
import IconOnlyButton from "~/components/buttons/IconOnlyButton";
import Image from "~/components/Image";
import BaseModal from "~/components/BaseModal";

import transition from "~/utilities/transition";

import { iconLinkColors } from "~/theme";

const iconStyles = ({ theme, color }) => css`
    ${transition({}, "color")};
    color: ${theme.iconLinkColors[color].color} !important;
    height: 40px;
    width: 40px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    align-items: center;

    &:hover,
    &:focus {
        ${color === "whiteAlt"
            ? ``
            : `color: ${theme.iconLinkColors[color].hover};`}
    }

    &:not(:last-child) {
        margin-right: 30px;
    }
`;

const IconLink = styled(IconOnlyLink)`
    ${iconStyles}
`;

const IconButton = styled(IconOnlyButton)`
    ${iconStyles}
`;

const ImageWrapper = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const CrossButton = styled(IconOnlyButton)`
    position: fixed;
    right: 3%;
    font-size: 8rem;
    color: ${({ theme }) => theme.palette.white};
    border: 2px solid transparent;

    &:focus,
    &:focus-visible {
        border: 2px solid ${({ theme }) => theme.palette.white};
    }
`;

const NavListItem = styled.li`
    list-style: none;
    margin: 0 5px -5px;

    outline: 2px solid transparent;
    border-radius: 100%;
    transition:
        outline-color 200ms ease-in-out 0ms,
        background-color 200ms ease-in-out 0ms;
    &:hover,
    &:focus {
        ${({ color }) =>
            color === "whiteAlt"
                ? `outline-color: white; background-color: rgb( 125 125 125 / .25);`
                : ``}
    }
`;

const PopupImage = ({
    color,
    screenReaderText,
    icon,
    iconLibrary,
    popupImage,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const closeModal = () => {
        setIsOpen(false);
    };

    const openModal = () => {
        setIsOpen(true);
    };

    return (
        <>
            <NavListItem color={color}>
                <IconButton
                    {...{
                        color,
                        icon,
                        screenReaderText,
                        iconLibrary,
                        onClick: openModal,
                    }}
                />
            </NavListItem>
            <BaseModal
                {...{
                    isOpen,
                    showIconButton: false,
                    overlayStyles: { backgroundColor: "rgba(0, 0, 0, .8)" },
                    contentStyles: {
                        maxWidth: "100%",
                        minHeight: "100%",
                        background: "none",
                        border: "none",
                        padding: "0",
                    },
                }}
            >
                <CrossButton
                    onClick={closeModal}
                    screenReaderText="Click to close"
                    iconLibrary="fas"
                    icon="times"
                />
                <ImageWrapper>
                    <Image {...popupImage} />
                </ImageWrapper>
            </BaseModal>
        </>
    );
};

const SocialLinks = ({ color, links }) => {
    return map(links, (link, index) => {
        const {
            icon,
            iconLibrary,
            linkUrl,
            screenReaderText,
            isPopup,
            popupImage,
        } = link;

        const libraryToUse = iconLibrary ? iconLibrary : "fab";

        if (isPopup && popupImage) {
            return (
                <PopupImage
                    {...{
                        color,
                        icon,
                        popupImage,
                        screenReaderText,
                        iconLibrary: libraryToUse,
                        key: `SocialLink-${index}`,
                    }}
                />
            );
        }

        return (
            <NavListItem key={`SocialLink-${index}`} {...{ color }}>
                <IconLink
                    {...{
                        color,
                        icon,
                        screenReaderText,
                        url: linkUrl,
                        iconLibrary: libraryToUse,
                    }}
                />
            </NavListItem>
        );
    });
};

SocialLinks.propTypes = {
    color: PropTypes.oneOf(Object.keys(iconLinkColors)),
    links: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.string.isRequired,
            iconLibrary: PropTypes.string,
            linkUrl: PropTypes.string.isRequired,
            screenReaderText: PropTypes.string.isRequired,
            isPopup: PropTypes.bool,
            popupImage: PropTypes.object,
        }).isRequired,
    ).isRequired,
};

export default SocialLinks;
